import { useEffect, useRef, useState } from 'react'
import { createTask, useProjects, useWorkspaces } from '../api/hooks.js'
import { fetchConversationThread } from '../api/graph.js'
import { unlockEncryption, UNLOCK } from '../auth/deviceUnlock.js'
import Select from '../components/Select.jsx'

/**
 * Form zum Erstellen eines FlawDesk-Tickets aus der aktiven Outlook-Mail.
 *
 * Lifecycle:
 *   - mount: laedt Workspaces des Users, waehlt den ersten als Default
 *   - workspace-Wechsel: laedt Projekte dieses Workspace
 *   - submit: POST /api/tasks, danach Success-View mit Ticket-Nr + Link
 *
 * #1537 E2E: ist der Workspace Ende-zu-Ende-verschluesselt und diese
 * (passwortlose Entra-)Session hat den Workspace-Key nicht entsperrt, antwortet
 * das Backend mit 409 workspace_encryption_required / hint=session_locked.
 * Dann faehrt ein geraete-gebundener Unlock-Flow hoch (bootstrap bzw. trust via
 * Recovery-Code), und das Anlegen wird transparent wiederholt.
 *
 * Initial-Werte:
 *   - Title  = Mail-Subject (truncated auf 160)
 *   - Body   = Mail-Plaintext mit Absender-Header
 */
export default function CreateTicketForm({ auth, mailContext, onClose, onCreated }) {
  const wsState = useWorkspaces(auth)

  // Default-Workspace-Auswahl: nimm den ersten Workspace nachdem die Liste da ist.
  const [workspaceId, setWorkspaceId] = useState('')
  useEffect(() => {
    if (wsState.status === 'ready' && wsState.workspaces.length > 0 && !workspaceId) {
      setWorkspaceId(wsState.workspaces[0].id)
    }
  }, [wsState.status, wsState.workspaces, workspaceId])

  const projState = useProjects(auth, workspaceId)

  const [projectKey, setProjectKey] = useState('')
  // Beim Workspace-Wechsel: Projekt-Auswahl zuruecksetzen
  useEffect(() => {
    setProjectKey('')
  }, [workspaceId])

  const [title, setTitle] = useState(() => (mailContext?.subject || '').slice(0, 160))
  const [description, setDescription] = useState(() => buildInitialBody(mailContext))
  const [priority, setPriority] = useState('medium')

  // Bewusst KEIN readBodyAsync mehr aus Office.js: dessen HTML->Text-Convert
  // (CoercionType.Text) klatscht Saetze zusammen, und auch der eigene
  // HTML->Text-Strip bekommt Outlook's <font>/<span>-Block-Strukturen nicht
  // sauber wieder in Zeilen aufgeloest. Stattdessen: der Body kommt beim
  // Submit aus dem Graph-Thread, wo wir bereits sauberen Plaintext bekommen
  // (Prefer: outlook.body-content-type="text") -- derselbe Body landet dann
  // sowohl in description als auch in task_mail_messages, konsistent.

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  // #1537: Zustand des E2E-Unlock-Overlays. null = aus.
  //   { phase: 'working' }                        -> Spinner
  //   { phase: 'show_recovery', recoveryCode }     -> Code einmalig anzeigen
  //   { phase: 'enter_recovery', error? }          -> Recovery-Code eingeben
  const [unlock, setUnlock] = useState(null)
  // Das beim Submit gebaute Payload, damit wir nach erfolgreichem Unlock
  // exakt dasselbe Anlegen wiederholen koennen (inkl. geladenem Mail-Thread).
  const pendingPayload = useRef(null)
  // #1537: nur EIN Unlock-Versuch pro Submit -- verhindert eine Endlosschleife,
  // wenn der WK auch nach dem Unlock nicht entsperrt werden kann (z.B. der User
  // hat fuer diesen Workspace gar keinen Member-Key -> Owner muss erst granten).
  const unlockAttempted = useRef(false)

  const canSubmit = title.trim().length > 0 && workspaceId && !submitting

  // Fuehrt das eigentliche Anlegen aus und behandelt den E2E-Sperrfall.
  const runCreate = async (payload) => {
    try {
      const result = await createTask(payload)
      if (onCreated) onCreated(result)
      if (onClose) onClose()
    } catch (err) {
      if (err?.code === 'workspace_encryption_required') {
        if (err?.body?.hint === 'session_locked') {
          if (unlockAttempted.current) {
            // Schon entsperrt und trotzdem gesperrt -> kein Member-Key fuer
            // diesen Workspace. Nicht erneut versuchen, sondern erklaeren.
            setError('Der Verschlüsselungs-Schlüssel konnte für dieses Gerät nicht entsperrt werden. Möglicherweise muss dir der Workspace-Owner den Zugriff auf diesen verschlüsselten Workspace erst freischalten.')
            setSubmitting(false)
            return
          }
          // Session-gebunden loesbar: Geraete-Unlock hochfahren, danach Retry.
          unlockAttempted.current = true
          pendingPayload.current = payload
          await beginUnlock()
          return
        }
        // workspace_unencrypted: vom Add-in nicht loesbar (Owner muss den WK
        // per Passwort-Login im Web einrichten). Klartext-Hinweis zeigen.
        setError(err?.body?.error || err?.message || 'Workspace-Verschluesselung nicht eingerichtet.')
        setSubmitting(false)
        return
      }
      setError(err?.message || 'Anlegen fehlgeschlagen')
      setSubmitting(false)
    }
  }

  // Startet den Unlock. force=true verwirft evtl. veraltete lokale Creds
  // (z.B. nachdem das Geraet serverseitig widerrufen wurde) und beschafft neu.
  const beginUnlock = async () => {
    setSubmitting(true)
    setUnlock({ phase: 'working' })
    try {
      const r = await unlockEncryption({ force: true })
      if (r.code === UNLOCK.BOOTSTRAPPED) {
        // Recovery-Code ist der einzige At-Rest-Anker -- erst zeigen, dann weiter.
        setUnlock({ phase: 'show_recovery', recoveryCode: r.recoveryCode })
        return
      }
      if (r.code === UNLOCK.NEEDS_RECOVERY) {
        setUnlock({ phase: 'enter_recovery' })
        return
      }
      if (r.code === UNLOCK.HAS_PASSWORD) {
        setUnlock(null)
        setSubmitting(false)
        setError('Dieses Konto hat ein Passwort. Bitte einmal auf flawdesk.de mit Passwort anmelden, um die Verschlüsselung zu entsperren.')
        return
      }
      // ALREADY / TRUSTED: Creds liegen vor -> direkt erneut anlegen.
      setUnlock(null)
      await runCreate(pendingPayload.current)
    } catch (e) {
      setUnlock(null)
      setSubmitting(false)
      setError(e?.message || 'Entsperren fehlgeschlagen')
    }
  }

  // Nutzer hat den frischen Recovery-Code gesichert -> Anlegen wiederholen.
  const confirmRecoveryDone = async () => {
    setUnlock(null)
    await runCreate(pendingPayload.current)
  }

  // Recovery-Code zum Vertrauen dieses Geraets eingereicht.
  const submitRecovery = async (code) => {
    const trimmed = (code || '').trim()
    if (!trimmed) {
      setUnlock({ phase: 'enter_recovery', error: 'Bitte den Recovery-Code eingeben.' })
      return
    }
    setUnlock({ phase: 'working' })
    try {
      const r = await unlockEncryption({ recoveryCode: trimmed })
      if (r.code === UNLOCK.TRUSTED) {
        setUnlock(null)
        await runCreate(pendingPayload.current)
        return
      }
      setUnlock({ phase: 'enter_recovery', error: 'Unerwartete Antwort beim Entsperren.' })
    } catch (e) {
      const msg = e?.code === 'bad_recovery'
        ? 'Recovery-Code ungültig.'
        : (e?.message || 'Entsperren fehlgeschlagen.')
      setUnlock({ phase: 'enter_recovery', error: msg })
    }
  }

  const cancelUnlock = () => {
    setUnlock(null)
    setSubmitting(false)
  }

  const handleSubmit = async () => {
    if (!canSubmit) return
    setError(null)
    setSubmitting(true)
    unlockAttempted.current = false

    const payload = {
      title: title.trim(),
      description: description.trim(),
      workspace_id: workspaceId,
      priority,
    }
    if (projectKey) payload.project_id = projectKey

    // #1088: Mail-Konversations-Thread via Graph holen wenn conversationId
    // vorhanden. Bei Fehler (Token, Network, Graph-Outage) das Ticket
    // trotzdem anlegen -- Thread ist Bonus, nicht blockierend.
    // Hard-Timeout 8s: wenn MSAL/Graph nicht antwortet, brechen wir ab
    // statt unendlich zu haengen.
    if (mailContext?.conversationId) {
      try {
        const threadPromise = fetchConversationThread(mailContext.conversationId)
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Thread-Load timeout (8s)')), 8000),
        )
        const mailThread = await Promise.race([threadPromise, timeoutPromise])
        if (Array.isArray(mailThread) && mailThread.length > 0) {
          payload.mail_thread = mailThread
          // eslint-disable-next-line no-console
          console.log(`[#1088] Thread geladen: ${mailThread.length} Nachrichten`)

          const initialBody = buildInitialBody(mailContext)
          if (description === initialBody && mailContext.internetMessageId) {
            const currentMail = mailThread.find(
              (m) => m.internet_message_id === mailContext.internetMessageId,
            )
            if (currentMail?.body_text) {
              payload.description = initialBody + cleanBody(currentMail.body_text)
            }
          }
        }
      } catch (threadErr) {
        // eslint-disable-next-line no-console
        console.warn('[#1088] Thread-Load fehlgeschlagen, Ticket wird ohne Mail-Verlauf angelegt:', threadErr)
      }
    }

    await runCreate(payload)
  }

  // ─── Unlock-Overlay (#1537) ─────────────────────────────────────
  if (unlock) {
    return (
      <div className="fd-form">
        <EncryptionUnlock
          state={unlock}
          onConfirmRecovery={confirmRecoveryDone}
          onSubmitRecovery={submitRecovery}
          onCancel={cancelUnlock}
        />
      </div>
    )
  }

  // ─── Form-View ──────────────────────────────────────────────────
  return (
    <div className="fd-form">

      <label className="fd-form-field">
        <span className="fd-form-label">Titel</span>
        <input
          className="fd-input"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
          placeholder="Was ist zu tun?"
          autoFocus
        />
      </label>

      <label className="fd-form-field">
        <span className="fd-form-label">Workspace</span>
        <Select
          value={workspaceId}
          onChange={(v) => setWorkspaceId(v)}
          disabled={wsState.status !== 'ready'}
          placeholder={
            wsState.status === 'loading' ? 'Lade Workspaces …'
            : wsState.status === 'error' ? 'Fehler beim Laden'
            : 'Workspace wählen'
          }
          options={
            wsState.status === 'ready'
              ? wsState.workspaces.map((ws) => ({ value: ws.id, label: ws.name }))
              : []
          }
        />
      </label>

      <label className="fd-form-field">
        <span className="fd-form-label">Projekt</span>
        <Select
          value={projectKey}
          onChange={(v) => setProjectKey(v)}
          disabled={!workspaceId || projState.status === 'loading'}
          placeholder={
            !workspaceId ? 'Erst Workspace wählen'
            : projState.status === 'loading' ? 'Lade Projekte …'
            : '— ohne Projekt —'
          }
          options={[
            { value: '', label: '— ohne Projekt —' },
            ...(projState.status === 'ready'
              ? projState.projects.map((p) => ({ value: p.key, label: p.label }))
              : []),
          ]}
        />
      </label>

      <label className="fd-form-field">
        <span className="fd-form-label">Priorität</span>
        <Select
          value={priority}
          onChange={(v) => setPriority(v)}
          options={[
            { value: 'low', label: 'Niedrig' },
            { value: 'medium', label: 'Mittel' },
            { value: 'high', label: 'Hoch' },
            { value: 'critical', label: 'Kritisch' },
          ]}
        />
      </label>

      <label className="fd-form-field">
        <span className="fd-form-label">Beschreibung</span>
        <textarea
          className="fd-input fd-textarea"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={8}
          placeholder="Kontext aus der Mail …"
        />
      </label>

      {error && (
        <div className="fd-form-error">{error}</div>
      )}

      <div className="fd-form-actions">
        <button
          className="fd-btn fd-btn--ghost"
          onClick={onClose}
          disabled={submitting}
        >
          Abbrechen
        </button>
        <button
          className="fd-btn fd-btn--primary"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {submitting ? 'Lege an…' : 'Ticket anlegen'}
        </button>
      </div>
    </div>
  )
}

/**
 * #1537: UI fuer den geraete-gebundenen E2E-Unlock. Drei Phasen:
 *   working        -> Spinner-Text
 *   show_recovery  -> frischen Recovery-Code EINMALIG anzeigen (muss bestaetigt werden)
 *   enter_recovery -> Recovery-Code eingeben (Identitaet existiert schon)
 */
function EncryptionUnlock({ state, onConfirmRecovery, onSubmitRecovery, onCancel }) {
  const [code, setCode] = useState('')
  const [copied, setCopied] = useState(false)

  if (state.phase === 'working') {
    return (
      <div className="fd-unlock">
        <h3 className="fd-form-label">Verschlüsselung wird entsperrt …</h3>
        <p>Einen Moment — dein Zugriffsschlüssel wird für dieses Gerät vorbereitet.</p>
      </div>
    )
  }

  if (state.phase === 'show_recovery') {
    const copy = async () => {
      try {
        await navigator.clipboard.writeText(state.recoveryCode || '')
        setCopied(true)
      } catch {
        setCopied(false)
      }
    }
    return (
      <div className="fd-unlock">
        <h3 className="fd-form-label">Recovery-Code sichern</h3>
        <p>
          Dieser Workspace ist Ende-zu-Ende-verschlüsselt. Für dein Konto wurde ein
          Zugriffsschlüssel eingerichtet. Der folgende Recovery-Code ist deine
          <strong> einzige Rettung</strong>, falls du den Zugang zu deinen Geräten
          verlierst. Er wird <strong>nur dieses eine Mal</strong> angezeigt.
        </p>
        <pre
          style={{
            userSelect: 'all',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            padding: '10px',
            borderRadius: '6px',
            background: 'rgba(127,127,127,0.12)',
            fontFamily: 'monospace',
          }}
        >
          {state.recoveryCode}
        </pre>
        <div className="fd-form-actions">
          <button className="fd-btn fd-btn--ghost" onClick={copy}>
            {copied ? 'Kopiert ✓' : 'Code kopieren'}
          </button>
          <button className="fd-btn fd-btn--primary" onClick={onConfirmRecovery}>
            Code gesichert — Ticket anlegen
          </button>
        </div>
      </div>
    )
  }

  // enter_recovery
  return (
    <div className="fd-unlock">
      <h3 className="fd-form-label">Gerät freischalten</h3>
      <p>
        Dein Konto hat bereits einen Verschlüsselungs-Schlüssel (z. B. über die
        Web-App eingerichtet). Gib deinen Recovery-Code ein, um dieses Gerät
        freizuschalten.
      </p>
      <label className="fd-form-field">
        <span className="fd-form-label">Recovery-Code</span>
        <input
          className="fd-input"
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="XXXXX-XXXXX-…"
          autoFocus
          autoComplete="off"
          spellCheck={false}
        />
      </label>
      {state.error && <div className="fd-form-error">{state.error}</div>}
      <div className="fd-form-actions">
        <button className="fd-btn fd-btn--ghost" onClick={onCancel}>
          Abbrechen
        </button>
        <button className="fd-btn fd-btn--primary" onClick={() => onSubmitRecovery(code)}>
          Freischalten
        </button>
      </div>
    </div>
  )
}

/**
 * Baut den initialen Description-Text aus dem Mail-Kontext.
 * Format: Kopf mit "Aus Mail" + Absender, dann leere Zeile.
 */
function buildInitialBody(mailContext) {
  if (!mailContext) return ''
  const lines = []
  if (mailContext.subject) {
    lines.push(`**Betreff:** ${mailContext.subject}`)
  }
  if (mailContext.from) {
    lines.push(`**Von:** ${mailContext.from}`)
  }
  if (lines.length > 0) {
    lines.push('')
    lines.push('---')
    lines.push('')
  }
  return lines.join('\n')
}

/**
 * Saeubert den Plaintext-Body aus Office.js fuer die Ticket-Beschreibung.
 *
 * Universelle Regeln, keine Provider-Spezifika:
 *   - CID-Inline-Image-Marker entfernen ([cid:image001.png@...])
 *   - Outlook-Plaintext "label<url>"-Format entkleben
 *   - Trailing-Whitespace pro Zeile abschneiden
 *   - Mehr als 2 Leerzeilen auf 2 normalisieren
 *   - Outer Whitespace trimmen
 *   - Cap auf 4000 Zeichen
 */
function cleanBody(raw) {
  if (!raw) return ''
  let s = raw
  s = s.replace(/\[cid:[^\]]+\]/g, '')
  s = s.replace(/<(?:https?:\/\/|mailto:)[^>]+>/g, '')
  s = s.split('\n').map((l) => l.replace(/\s+$/, '')).join('\n')
  s = s.replace(/\n{3,}/g, '\n\n')
  s = s.trim()
  if (s.length > 4000) {
    return s.slice(0, 4000).trimEnd() + '\n\n_… (gekürzt, Mail weiter im Outlook)_'
  }
  return s
}
